/**
 * Compact Subscription Dropdown
 * Converts radio button subscription frequencies into a dropdown
 * Supports product-specific and variant-specific default frequency
 * Preserves subscription selection across variant changes
 */

(function() {
  'use strict';

  // Configuration
  const DEFAULT_FREQUENCY = '1'; // Default to 1 month if no product-specific default

  // State management
  let subscriptionState = {
    isSubscription: true, // Default to Subscribe & Save
    lastFrequency: null
  };

  /**
   * Get variant-specific or product-specific default frequency
   * Checks for variant metafield first, then product metafield, or falls back to DEFAULT_FREQUENCY
   */
  function getDefaultFrequency() {
    // Get current variant ID from hidden input field (Shopify stores the numeric variant ID here)
    let variantId = null;
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput && variantInput.value) {
      variantId = variantInput.value;
    }

    // Fallback to URL parameter if input not found
    if (!variantId) {
      const urlParams = new URLSearchParams(window.location.search);
      variantId = urlParams.get('variant');
    }

    // Check for variant-specific metafield (stored as data attribute)
    if (variantId) {
      const variantData = document.querySelector(`[data-variant-id="${variantId}"]`);
      if (variantData && variantData.dataset.subscriptionFrequency) {
        console.log('[Subscription State] Found variant-specific frequency:', variantData.dataset.subscriptionFrequency, 'for variant:', variantId);
        return variantData.dataset.subscriptionFrequency;
      }
    }

    // Check for product-level metafield
    const productMetafield = document.querySelector('[data-subscription-default-frequency]');
    if (productMetafield) {
      console.log('[Subscription State] Using product-level frequency:', productMetafield.getAttribute('data-subscription-default-frequency'));
      return productMetafield.getAttribute('data-subscription-default-frequency');
    }

    // Fallback to global default
    console.log('[Subscription State] Using global default frequency:', DEFAULT_FREQUENCY);
    return DEFAULT_FREQUENCY;
  }

  /**
   * Save current subscription state (subscribe vs one-time)
   */
  function saveSubscriptionState() {
    // Look for the visible (non-hidden) subscription widget
    const visibleWidget = document.querySelector('.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden)');
    if (!visibleWidget) {
      console.log('[Subscription State] No visible widget found to save state');
      return;
    }

    // Check which radio button is currently checked
    const oneTimeRadio = visibleWidget.querySelector('input[type="radio"][id*="one_time_purchase"]:checked');
    const subscribeRadio = visibleWidget.querySelector('input[type="radio"]:not([id*="one_time_purchase"]):checked');

    if (oneTimeRadio) {
      subscriptionState.isSubscription = false;
      console.log('[Subscription State] Saved: One-time purchase');
    } else if (subscribeRadio) {
      subscriptionState.isSubscription = true;

      // Save frequency from the dropdown if visible
      const dropdownTrigger = visibleWidget.querySelector('.subscription-dropdown-trigger');
      if (dropdownTrigger) {
        subscriptionState.lastFrequency = 'Deliver every ' + dropdownTrigger.textContent.trim();
      }

      console.log('[Subscription State] Saved: Subscribe & Save, frequency:', subscriptionState.lastFrequency);
    } else {
      // Default to subscription if nothing is checked yet
      subscriptionState.isSubscription = true;
      console.log('[Subscription State] No radio checked, defaulting to subscription');
    }
  }

  /**
   * Restore subscription state after variant change
   */
  function restoreSubscriptionState() {
    console.log('[Subscription State] Restoring state:', subscriptionState);

    // Use longer timeout and polling to ensure widget is fully loaded
    let attempts = 0;
    const maxAttempts = 20;

    const restoreInterval = setInterval(function() {
      attempts++;

      // Look for the visible subscription widget (not hidden)
      const visibleWidget = document.querySelector('.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden)');
      if (!visibleWidget) {
        console.log('[Subscription State] Waiting for widget... attempt', attempts);
        if (attempts >= maxAttempts) {
          clearInterval(restoreInterval);
          console.log('[Subscription State] Widget not found after max attempts');
        }
        return;
      }

      clearInterval(restoreInterval);
      console.log('[Subscription State] Widget found, restoring...');

      // Now restore the state by clicking the subscription label wrapper
      // Shopify's widget uses click handlers on the wrapper elements, not the radio buttons
      if (subscriptionState.isSubscription) {
        // Find the "Subscribe & Save" label (NOT one-time purchase)
        const subscriptionLabels = visibleWidget.querySelectorAll('.shopify_subscriptions_app_block_label');
        let subscribeLabel = null;

        subscriptionLabels.forEach(label => {
          if (!label.classList.contains('shopify_subscriptions_purchase_option_wrapper')) {
            subscribeLabel = label;
          }
        });

        if (subscribeLabel && !subscribeLabel.classList.contains('active')) {
          console.log('[Subscription State] Clicking Subscribe & Save label wrapper');
          const wrapper = subscribeLabel.querySelector('.shopify_subscriptions_purchase_option_wrapper');
          if (wrapper) {
            wrapper.click();

            // After activating subscription, set the variant-specific frequency
            setTimeout(function() {
              const variantFrequency = getDefaultFrequency();
              console.log('[Subscription State] Setting variant-specific frequency:', variantFrequency);

              // Find and click the matching frequency option
              const frequencyList = subscribeLabel.querySelector('.shopify_subscriptions_app_block_label_children');
              if (frequencyList) {
                const frequencyOptions = frequencyList.querySelectorAll('li');
                let matchFound = false;

                frequencyOptions.forEach(option => {
                  const label = option.querySelector('label');
                  if (label) {
                    const labelText = label.textContent.trim();

                    // Match the frequency: "1" matches "every month", "2" matches "every 2 months", etc.
                    if (variantFrequency === '1' && labelText.match(/every\s+month(?!\s*\d)/i)) {
                      console.log('[Subscription State] Matched frequency:', labelText);
                      label.click();
                      matchFound = true;
                    } else if (variantFrequency !== '1' && labelText.includes('every ' + variantFrequency + ' month')) {
                      console.log('[Subscription State] Matched frequency:', labelText);
                      label.click();
                      matchFound = true;
                    }
                  }
                });

                if (!matchFound) {
                  console.log('[Subscription State] No frequency match found for:', variantFrequency);
                }
              }
            }, 300);
          }
        }
      } else {
        // Find and click "One-time purchase" label wrapper
        const oneTimeLabel = visibleWidget.querySelector('.shopify_subscriptions_app_block_label.shopify_subscriptions_purchase_option_wrapper');
        if (oneTimeLabel && !oneTimeLabel.classList.contains('active')) {
          console.log('[Subscription State] Clicking One-time purchase label wrapper');
          const wrapper = oneTimeLabel.querySelector('.shopify_subscriptions_purchase_option_wrapper');
          if (wrapper) {
            wrapper.click();
          }
        }
      }
    }, 100); // Check every 100ms
  }

  /**
   * Ensure Subscribe & Save is selected by default on page load
   */
  function setDefaultSubscription() {
    setTimeout(function() {
      const subscriptionBlocks = document.querySelectorAll('.shopify_subscriptions_app_block_label');
      let subscriptionBlock = null;

      // Find the subscription block (not one-time purchase)
      subscriptionBlocks.forEach(block => {
        if (!block.classList.contains('shopify_subscriptions_purchase_option_wrapper')) {
          subscriptionBlock = block;
        }
      });

      if (subscriptionBlock && !subscriptionBlock.classList.contains('active')) {
        const wrapper = subscriptionBlock.querySelector('.shopify_subscriptions_purchase_option_wrapper');
        if (wrapper) {
          wrapper.click();
          subscriptionState.isSubscription = true;
        }
      }
    }, 500);
  }

  /**
   * Initialize subscription dropdown for a specific block
   */
  function initSubscriptionDropdownForBlock(subscriptionBlock) {
    if (!subscriptionBlock) return;

    const frequencyList = subscriptionBlock.querySelector('.shopify_subscriptions_app_block_label_children');
    if (!frequencyList) return;

    const frequencyOptions = frequencyList.querySelectorAll('li');
    if (frequencyOptions.length === 0) return;

    // Check if dropdown already initialized
    if (subscriptionBlock.querySelector('.subscription-dropdown-trigger')) {
      return; // Already initialized
    }

    // Get default frequency for this product/variant
    const defaultFrequency = getDefaultFrequency();

    // Create dropdown trigger element
    const dropdownTrigger = document.createElement('div');
    dropdownTrigger.className = 'subscription-dropdown-trigger';

    // Create wrapper for layout
    const wrapper = document.createElement('div');
    wrapper.className = 'subscription-frequency-wrapper';

    const label = document.createElement('span');
    label.className = 'subscription-frequency-label';
    label.textContent = 'Deliver every';

    // Find and set default option
    let selectedOption = frequencyOptions[0]; // Fallback to first option
    frequencyOptions.forEach((option, index) => {
      const input = option.querySelector('input[type="radio"]');
      const label = option.querySelector('label');

      if (input && label) {
        const labelText = label.textContent.trim();

        // Check if this matches the default frequency
        // Handle "Deliver every month" (1) or "Deliver every 2 months"
        if (defaultFrequency === '1' && labelText.match(/every\s+month(?!\s*\d)/i)) {
          selectedOption = option;
        } else if (labelText.includes('every ' + defaultFrequency + ' month')) {
          selectedOption = option;
        }
      }
    });

    // Set initial text
    updateDropdownText(dropdownTrigger, selectedOption);

    // Insert dropdown trigger before frequency list
    wrapper.appendChild(label);
    wrapper.appendChild(dropdownTrigger);
    frequencyList.parentNode.insertBefore(wrapper, frequencyList);

    // Move the policy text (Auto-renews...) into the active subscription label
    const policyText = subscriptionBlock.closest('.shopify_subscriptions_fieldset').querySelector('.shopify_subscriptions_app_policy');
    if (policyText && subscriptionBlock.classList.contains('active')) {
      subscriptionBlock.appendChild(policyText);
    }

    // Add click handler to toggle dropdown
    dropdownTrigger.addEventListener('click', function(e) {
      e.stopPropagation();
      dropdownTrigger.classList.toggle('open');
      frequencyList.classList.toggle('show-dropdown');
    });

    // Add click handlers to frequency options
    frequencyOptions.forEach((option, index) => {
      const input = option.querySelector('input[type="radio"]');
      const label = option.querySelector('label');

      if (input && label) {
        label.addEventListener('click', function(e) {
          // Don't prevent default - let Shopify handle the radio button
          e.stopPropagation();

          // Update selected option
          frequencyOptions.forEach(opt => opt.classList.remove('selected'));
          option.classList.add('selected');

          // Update dropdown text
          updateDropdownText(dropdownTrigger, option);

          // Close dropdown
          dropdownTrigger.classList.remove('open');
          frequencyList.classList.remove('show-dropdown');
        });
      }
    });

    // Click outside to close dropdown
    document.addEventListener('click', function(e) {
      if (!subscriptionBlock.contains(e.target)) {
        dropdownTrigger.classList.remove('open');
        frequencyList.classList.remove('show-dropdown');
      }
    });

    // Set default frequency on page load (with null check)
    const defaultInput = selectedOption.querySelector('input[type="radio"]');
    if (defaultInput) {
      defaultInput.checked = true;
      selectedOption.classList.add('selected');
      defaultInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Update dropdown trigger text based on selected option
   */
  function updateDropdownText(trigger, option) {
    const label = option.querySelector('label');
    if (!label) return;

    let text = label.textContent.trim();

    // Extract frequency: "Deliver every 2 months" → "2 months"
    // Or "Deliver every month" → "1 month"
    const multiMonthMatch = text.match(/every\s+(\d+)\s+months?/i);
    if (multiMonthMatch) {
      const num = multiMonthMatch[1];
      trigger.textContent = num + (num === '1' ? ' month' : ' months');
    } else if (text.match(/every\s+month/i)) {
      // Single month without number
      trigger.textContent = '1 month';
    } else {
      trigger.textContent = text;
    }
  }

  /**
   * Initialize all subscription dropdowns on the page
   */
  function initAllSubscriptionDropdowns() {
    const subscriptionBlocks = document.querySelectorAll('.shopify_subscriptions_app_block_label.active');
    subscriptionBlocks.forEach(block => {
      initSubscriptionDropdownForBlock(block);
    });
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    // Wait for subscription widget to load
    const checkInterval = setInterval(function() {
      const subscriptionBlocks = document.querySelectorAll('.shopify_subscriptions_app_block_label.active');
      if (subscriptionBlocks.length > 0) {
        clearInterval(checkInterval);
        initAllSubscriptionDropdowns();
      }
    }, 100);

    // Stop checking after 10 seconds
    setTimeout(function() {
      clearInterval(checkInterval);
    }, 10000);
  }

  /**
   * Watch for subscription blocks becoming active
   * This handles when user clicks "Subscribe & Save" radio button
   */
  function watchForActiveBlocks() {
    // Use MutationObserver to watch for class changes
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.classList.contains('shopify_subscriptions_app_block_label') &&
              target.classList.contains('active')) {
            // Block became active, initialize dropdown if not already done
            initSubscriptionDropdownForBlock(target);

            // Update subscription state when user manually switches
            saveSubscriptionState();
          }
        }
      });
    });

    // Observe all subscription blocks for class changes
    document.querySelectorAll('.shopify_subscriptions_app_block_label').forEach(block => {
      observer.observe(block, { attributes: true, attributeFilter: ['class'] });
    });

    // Also observe document for new subscription blocks being added
    const documentObserver = new MutationObserver(function(mutations) {
      const newBlocks = document.querySelectorAll('.shopify_subscriptions_app_block_label:not([data-dropdown-watched])');
      newBlocks.forEach(block => {
        block.setAttribute('data-dropdown-watched', 'true');
        observer.observe(block, { attributes: true, attributeFilter: ['class'] });

        // If already active, initialize immediately
        if (block.classList.contains('active')) {
          initSubscriptionDropdownForBlock(block);
        }
      });
    });

    documentObserver.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Listen for variant changes from the product page
   */
  function watchForVariantChanges() {
    // Listen for variant radio button clicks
    document.addEventListener('click', function(e) {
      const variantLabel = e.target.closest('variant-radios label');
      if (variantLabel) {
        // Save state before variant changes
        saveSubscriptionState();
        console.log('[Subscription State] Saved before variant change:', subscriptionState);
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      init();
      watchForActiveBlocks();
      watchForVariantChanges();
      setDefaultSubscription(); // Ensure Subscribe & Save is selected on page load
    });
  } else {
    init();
    watchForActiveBlocks();
    watchForVariantChanges();
    setDefaultSubscription(); // Ensure Subscribe & Save is selected on page load
  }

  // Re-initialize when variant changes (Shopify may reload subscription widget)
  // Listen for both event formats
  document.addEventListener('variant:change', function(e) {
    console.log('[Subscription State] variant:change event fired', e.detail);
    init();
    watchForActiveBlocks();
    restoreSubscriptionState(); // Restore user's previous selection
  });

  document.addEventListener('variant-change', function(e) {
    console.log('[Subscription State] variant-change event fired', e.detail);
    init();
    watchForActiveBlocks();
    restoreSubscriptionState(); // Restore user's previous selection
  });

  // Also listen for DOM changes in the subscription widget container
  // This is the main restoration mechanism since variant change events don't always fire
  const subscriptionObserver = new MutationObserver(function(mutations) {
    let shouldRestore = false;

    mutations.forEach(function(mutation) {
      // Check if subscription widget was added/modified
      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1 && node.classList && node.classList.contains('shopify_subscriptions_app_block')) {
            console.log('[Subscription State] Subscription widget DOM added/changed');
            shouldRestore = true;
          }
        });
      }

      // Also check if existing subscription blocks changed visibility
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const target = mutation.target;
        if (target.classList && target.classList.contains('shopify_subscriptions_app_block')) {
          // Widget visibility changed (hidden/visible toggled on variant change)
          const wasHidden = mutation.oldValue && mutation.oldValue.includes('shopify_subscriptions_app_block--hidden');
          const isNowVisible = !target.classList.contains('shopify_subscriptions_app_block--hidden');

          if (wasHidden && isNowVisible) {
            console.log('[Subscription State] Subscription widget became visible (variant changed)');
            shouldRestore = true;
          }
        }
      }
    });

    if (shouldRestore) {
      restoreSubscriptionState();
    }
  });

  // Observe the main product area for subscription widget changes
  const productInfo = document.querySelector('product-info');
  if (productInfo) {
    subscriptionObserver.observe(productInfo, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['class']
    });
  }
})();
